// ---------------------------------------------------------------------------
// Token Manager Factory
//
// Creates an isolated token manager instance per portal. Each portal calls
// createTokenManager({ namespace, refreshUrl }) in its own auth/index.ts.
//
// The access token is stored in JS module memory (not localStorage).
// The refresh token is an httpOnly cookie set by the backend — the frontend
// cannot read or write it. The browser sends it automatically when
// credentials: 'include' is used and the request URL matches the cookie Path.
// ---------------------------------------------------------------------------

export interface TokenManagerConfig {
  /** Human-readable namespace for debugging (e.g. 'company', 'employee') */
  namespace: string;
  /** Full path for the refresh endpoint, e.g. '/api/v1/auth/refresh' */
  refreshUrl: string;
}

export interface TokenManager {
  getToken(): string | null;
  setToken(accessToken: string): void;
  clearToken(): void;
  isTokenValid(): boolean;
  getValidToken(): Promise<string | null>;
  forceRefresh(): Promise<string | null>;
  onSessionExpired(cb: () => void): void;
  triggerSessionExpired(): void;
}

// ---------------------------------------------------------------------------
// JWT helpers (no library needed — we only need the `exp` claim)
// ---------------------------------------------------------------------------

function decodeJwtExpiry(jwt: string): number | null {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]));
    if (typeof payload.exp === 'number') {
      return payload.exp * 1000; // convert Unix seconds → ms
    }
    return null;
  } catch {
    return null;
  }
}

export interface AccessTokenClaims {
  sub: string | null;
  sessionId: string | null;
  email: string | null;
  role: string | null;
  expiresAt: number | null;
}

/**
 * Decodes the claims embedded in an access token JWT. The login/verify-otp
 * response body only ever carries `{ accessToken }` — `sessionId`/`role`/etc.
 * are signed into the token itself instead, so callers that need them (e.g.
 * to persist the session id, or to know the user's role right after login)
 * read them from here rather than from the API response.
 */
export function decodeAccessTokenClaims(jwt: string): AccessTokenClaims {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]));
    return {
      sub: payload.sub ?? null,
      sessionId: payload.sessionId ?? null,
      email: payload.email ?? null,
      role: payload.role ?? null,
      expiresAt: typeof payload.exp === 'number' ? payload.exp * 1000 : null,
    };
  } catch {
    return {
      sub: null,
      sessionId: null,
      email: null,
      role: null,
      expiresAt: null,
    };
  }
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

const EXPIRY_BUFFER_MS = 60_000; // start refreshing 60s before expiry

export function createTokenManager(config: TokenManagerConfig): TokenManager {
  let accessToken: string | null = null;
  let tokenExpiresAt: number | null = null;
  let refreshPromise: Promise<string | null> | null = null;
  let sessionExpiredCallback: (() => void) | null = null;

  function getToken(): string | null {
    return accessToken;
  }

  function setToken(token: string): void {
    accessToken = token;
    tokenExpiresAt = decodeJwtExpiry(token);
  }

  function clearToken(): void {
    accessToken = null;
    tokenExpiresAt = null;
    refreshPromise = null;
  }

  function isTokenValid(): boolean {
    if (!accessToken || tokenExpiresAt === null) return false;
    const now = Date.now();
    const valid = now < tokenExpiresAt - EXPIRY_BUFFER_MS;
    return valid;
  }

  function onSessionExpired(cb: () => void): void {
    sessionExpiredCallback = cb;
  }

  function triggerSessionExpired(): void {
    clearToken();
    sessionExpiredCallback?.();
  }

  async function performRefresh(): Promise<string | null> {
    try {
      const res = await fetch(config.refreshUrl, {
        method: 'POST',
        credentials: 'include', // sends the httpOnly refresh_token cookie
        headers: {
          'Content-Type': 'application/json',
          Portal: config.namespace,
          'x-platform-type': 'web',
        },
      });

      if (!res.ok) return null;

      const body = (await res.json()) as { data: { accessToken: string } };
      const newToken = body?.data?.accessToken;
      if (!newToken) return null;

      setToken(newToken);
      return newToken;
    } catch {
      return null;
    }
  }

  function refreshNow(): Promise<string | null> {
    // Join an in-flight refresh instead of firing a second request
    if (refreshPromise) return refreshPromise;

    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });

    return refreshPromise;
  }

  async function getValidToken(): Promise<string | null> {
    if (isTokenValid()) return accessToken;
    return refreshNow();
  }

  // Forces a network refresh even if the cached token is still valid — used
  // by the MCP OAuth bridge to (re)issue the mcp_bridge_token cookie for an
  // already-authenticated user without making them re-submit their password.
  function forceRefresh(): Promise<string | null> {
    return refreshNow();
  }

  return {
    getToken,
    setToken,
    clearToken,
    isTokenValid,
    getValidToken,
    forceRefresh,
    onSessionExpired,
    triggerSessionExpired,
  };
}
